import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { auditAPI } from '../../services/api';
import { Shield, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';

const PAGE_SIZE = 10;

const AuditPage = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  useEffect(() => {
    loadLogs();
  }, []);

  const loadLogs = async () => {
    try {
      const response = await auditAPI.getLog();
      setLogs(response.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(logs.length / PAGE_SIZE));
  const paginated = logs.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>
              Auditoría del Sistema
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              Registro de ejecuciones del scheduler
            </p>
          </div>
          {logs.length > 0 && (
            <span className="text-sm text-slate-500" data-testid="audit-total-count">{logs.length} registros</span>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" />
          </div>
        ) : logs.length === 0 ? (
          <Card className="border border-slate-200">
            <CardContent className="flex flex-col items-center justify-center h-64 text-center">
              <Shield className="w-12 h-12 text-slate-300 mb-4" />
              <p className="text-slate-500">No hay registros de auditoría</p>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="data-table" data-testid="audit-table">
                <thead>
                  <tr>
                    <th>Fecha/Hora</th>
                    <th>Semana</th>
                    <th>Asignados</th>
                    <th>Tipo</th>
                    <th>Semilla</th>
                    <th>Reglas</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.map((log) => (
                    <tr key={log.id}>
                      <td className="text-sm">{log.executed_at ? new Date(log.executed_at).toLocaleString('es-CL', { timeZone: 'America/Santiago', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}</td>
                      <td>{log.week_start_date}</td>
                      <td className="font-medium">{log.total_assigned}</td>
                      <td>
                        <span className={log.is_simulation ? 'badge-warning' : 'badge-success'}>
                          {log.is_simulation ? 'Simulación' : 'Real'}
                        </span>
                      </td>
                      <td className="font-mono text-xs text-slate-500">{log.seed}</td>
                      <td className="text-sm text-slate-600">{log.rules_applied?.join(', ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-slate-200 pt-4" data-testid="audit-pagination">
                <p className="text-sm text-slate-500">
                  {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, logs.length)} de {logs.length}
                </p>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8 rounded-sm"
                    disabled={page === 1}
                    onClick={() => setPage(page - 1)}
                    data-testid="audit-prev-page"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                    <Button
                      key={p}
                      variant={p === page ? 'default' : 'outline'}
                      size="icon"
                      className={`h-8 w-8 rounded-sm text-xs ${p === page ? 'bg-[#002FA7] hover:bg-[#001B66]' : ''}`}
                      onClick={() => setPage(p)}
                      data-testid={`audit-page-${p}`}
                    >
                      {p}
                    </Button>
                  ))}
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8 rounded-sm"
                    disabled={page === totalPages}
                    onClick={() => setPage(page + 1)}
                    data-testid="audit-next-page"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default AuditPage;
